import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from './routes';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingSpinner } from '@/components/ui';
import { HomeRedirect } from './HomeRedirect';
import UpdateProfilePage from '@/pages/auth/UpdateProfileForm';
import { superadminRoutes } from '@/superadmin/routes/SuperadminRouter';

// Lazy load components for code splitting
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const TeamLoginPage = lazy(() => import('@/pages/auth/TeamLoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const ChangePasswordPage = lazy(() => import('@/pages/auth/ChangePasswordPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const TransactionsPage = lazy(() => import('@/pages/merchant/Transactions'));
const WebhooksPage = lazy(() => import('@/pages/merchant/Webhooks/pages/WebhooksPage'));
const TeamsPage = lazy(() => import('@/pages/merchant/Teams/pages/TeamsPage/TeamsPage'));
const SettlementsPage = lazy(() => import('@/pages/merchant/Settlements'));
const TestPage = lazy(() => import('@/pages/merchant/Test/testPage'));
const WebhookDetailPage = lazy(
  () => import('@/pages/merchant/Webhooks/pages/WebhookDetailPage/WebhookDetailPage')
);
const TeamUserDashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const TeamUserTransactionsPage = lazy(() => import('@/pages/merchant/Transactions'));


const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Layouts
const MainLayout = lazy(() => import('@/layouts/MainLayout'));
const AuthLayout = lazy(() => import('@/layouts/AuthLayout'));

// Wrapper component for lazy-loaded routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense
    fallback={
      <LoadingSpinner
        title="Loading Unity..."
        subtitle="Please wait while we prepare your experience"
        size="medium"
        color="white"
      />
    }
  >
    {children}
  </Suspense>
);

// Create router configuration
// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: <HomeRedirect />,
  },
  {
    path: '/auth',
    element: (
      <LazyRoute>
        <AuthLayout />
      </LazyRoute>
    ),
    children: [
      {
        path: 'login',
        element: (
          <LazyRoute>
            <LoginPage />
          </LazyRoute>
        ),
      },
      {
        path: 'team-login/:teamId',
        element: (
          <LazyRoute>
            <TeamLoginPage />
          </LazyRoute>
        ),
      },
      {
        path: 'register',
        element: (
          <LazyRoute>
            <RegisterPage />
          </LazyRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <LazyRoute>
            <ForgotPasswordPage />
          </LazyRoute>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <LazyRoute>
            <ResetPasswordPage />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <MainLayout />
        </LazyRoute>
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/dashboard',
        index: true,
        element: (
          <LazyRoute>
            <DashboardPage />
          </LazyRoute>
        ),
      },
      {
        path: '/transactions',
        element: (
          <LazyRoute>
            <TransactionsPage />
          </LazyRoute>
        ),
      },
      {
        path: '/webhooks',
        element: (
          <LazyRoute>
            <WebhooksPage />
          </LazyRoute>
        ),
      },
      {
        path: '/webhooks/:webhookId',
        element: (
          <LazyRoute>
            <WebhookDetailPage />
          </LazyRoute>
        ),
      },
      {
        path: '/merchant',
        children: [
          {
            path: 'transactions',
            element: (
              <LazyRoute>
                <TransactionsPage />
              </LazyRoute>
            ),
          },
          {
            path: 'webhooks',
            element: (
              <LazyRoute>
                <WebhooksPage />
              </LazyRoute>
            ),
          },
          {
            path: 'webhooks/:webhookId',
            element: (
              <LazyRoute>
                <WebhookDetailPage />
              </LazyRoute>
            ),
          },
          {
            path: 'teams',
            element: (
              <LazyRoute>
                <TeamsPage />
              </LazyRoute>
            ),
          },
          {
            path: 'settlements',
            element: (
              <LazyRoute>
                <SettlementsPage />
              </LazyRoute>
            ),
          },
          {
            path: 'test',
            element: (
              <LazyRoute>
                <TestPage />
              </LazyRoute>
            ),
          },
        ],
      },
      {
        path: 'profile',
        element: (
          <LazyRoute>
            <UpdateProfilePage />
          </LazyRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyRoute>
            <div>Settings Page - Coming Soon</div>
          </LazyRoute>
        ),
      },
      {
        path: 'change-password',
        element: (
          <LazyRoute>
            <ChangePasswordPage />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    path: '/teamUser',
    element: (
      <ProtectedRoute>
        <LazyRoute>
          <MainLayout />
        </LazyRoute>
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <LazyRoute>
            <TeamUserDashboardPage />
          </LazyRoute>
        ),
      },
      {
        path: 'transactions',
        element: (
          <LazyRoute>
            <TeamUserTransactionsPage />
          </LazyRoute>
        ),
      },
    ],
  },

  {
    path: ROUTES.LOGIN,
    element: <Navigate to="/auth/login" replace />,
  },
  {
    path: ROUTES.REGISTER,
    element: <Navigate to="/auth/register" replace />,
  },
  {
    path: ROUTES.NOT_FOUND,
    element: (
      <LazyRoute>
        <NotFoundPage />
      </LazyRoute>
    ),
  },
  {
    path: ROUTES.UNAUTHORIZED,
    element: (
      <LazyRoute>
        <div>Unauthorized - Access Denied</div>
      </LazyRoute>
    ),
  },
  // Superadmin routes
  ...superadminRoutes,
  {
    path: '*',
    element: <Navigate to={ROUTES.NOT_FOUND} replace />,
  },
]);

const AppRouter: React.FC = () => {
  return null; // Router is handled by RouterProvider in App.tsx
};

export default AppRouter;
